import { computed, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useFilingStore } from '@/modules/filing/stores/filing-store'
import { useTaskListStore } from '@/modules/tasking/stores/tasking-store'
import { initialMoney, type Money } from '@/modules/shared/utils/money'
import { format } from '@/modules/shared/utils/v-table'
import { rails_url } from '@/modules/shared/utils/rails'

import { useEntityStore, type MetricSeriesPoint } from '@/modules/investing/stores/better-entity-store'
import { useCapitalAccountStore } from '@/modules/investing/stores/capital-account-store'
import { useTransactionStore } from '@/modules/investing/stores/transaction-store'
import { useChartInsights } from '@/modules/investing/composables/use-chart-insights'

export type LifecycleState = 'raising' | 'closed' | 'exited'

export const useEntityOverview = () => {
  const route = useRoute()
  const { entity_id, slug, entity_type } = route.params as {
    entity_id: string
    slug: string
    entity_type: 'spv' | 'fund'
  }

  const entityStore = useEntityStore()
  const capitalAccountStore = useCapitalAccountStore()
  const transactionStore = useTransactionStore()
  const filingStore = useFilingStore()
  const authStore = useAuthStore()
  const taskListStore = useTaskListStore()
  const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

  const entity = computed(() => entityStore.entityExpanded)
  const isAdmin = computed(
    () => authStore.is_site_or_group_admin || !!entityStore.getAdmin(authStore.current_user.investor_id),
  )
  const isSiteAdmin = computed(() => authStore.is_site_admin)
  const taskListId = computed(() => (entity.value as any)?.tasking_task_list_id ?? null)
  const isBillingSettled = computed(() => (entity.value as any)?.is_billing_settled ?? false)

  const lifecycleState = computed<LifecycleState>(
    () => ((entity.value as any)?.lifecycle_state as LifecycleState) ?? 'closed',
  )
  const isRaising = computed(() => lifecycleState.value === 'raising')
  const isClosed = computed(() => lifecycleState.value === 'closed')
  const isExited = computed(() => lifecycleState.value === 'exited')

  const dealCard = computed(() => (entity.value as any)?.deal_card ?? null)
  const structure = computed(() => ({
    managementFee: format(entity.value?.structure?.management_fee_rate, 'percent'),
    carriedInterest: format(entity.value?.structure?.carried_interest_rate, 'percent'),
    preferredReturn: format(entity.value?.structure?.preferred_return_rate, 'percent'),
  }))
  const shareholders = computed(() => entity.value?.shareholders ?? [])
  const isMemberView = computed(() => 'shareholders' in (entityStore.entity ?? {}))
  const hasStructure = computed(() => !!entity.value?.structure)
  const showMetrics = computed(() => isMemberView.value || !!entityStore.entity?.insights)
  const investorCount = computed(() => shareholders.value.length)
  const investmentCompletedCount = computed<number>(() => entity.value?.insights?.portfolio_count ?? 0)

  const companyHref = computed(() => {
    const id = dealCard.value?.company_id
    return id ? `${rails_url()}/companies/${id}` : null
  })

  const dealCardFolderId = computed(() => dealCard.value?.filing_folder_id ?? null)
  const dealFiles = computed(() =>
    dealCardFolderId.value ? filingStore.files.filter((f) => f.filing_folder_id === dealCardFolderId.value) : [],
  )

  const closingDocumentsFolder = computed(() =>
    dealCardFolderId.value
      ? filingStore.folders.find(
          (f) => f.parent_id === dealCardFolderId.value && f.system_folder === 'closing_documents',
        )
      : undefined,
  )
  const closingDocuments = computed(() =>
    closingDocumentsFolder.value
      ? filingStore.files.filter((f) => f.filing_folder_id === closingDocumentsFolder.value!.id)
      : [],
  )
  const loadClosingDocuments = async () => {
    if (!dealCardFolderId.value) return
    await filingStore.fetchFolders({ parent_id: dealCardFolderId.value })
    if (closingDocumentsFolder.value) {
      await filingStore.fetchFiles(closingDocumentsFolder.value.id)
    }
  }

  const exitDocumentsFolder = computed(() =>
    dealCardFolderId.value
      ? filingStore.folders.find((f) => f.parent_id === dealCardFolderId.value && f.system_folder === 'exit_documents')
      : undefined,
  )
  const exitDocuments = computed(() =>
    exitDocumentsFolder.value
      ? filingStore.files.filter((f) => f.filing_folder_id === exitDocumentsFolder.value!.id)
      : [],
  )
  const loadExitDocuments = async () => {
    if (!dealCardFolderId.value) return
    await filingStore.fetchFolders({ parent_id: dealCardFolderId.value })
    if (exitDocumentsFolder.value) {
      await filingStore.fetchFiles(exitDocumentsFolder.value.id)
    }
  }

  const metricSeriesPoints = computed<MetricSeriesPoint[]>(() => entityStore.metricSeries?.points ?? [])
  const metricSeriesCurrency = computed(() => entityStore.metricSeries?.currency ?? entity.value?.currency ?? 'USD')
  const metricSeriesLoading = computed(() => entityStore.metricSeriesLoading)

  const totalCommitted = computed<Money>(() => entity.value?.insights?.committed ?? initialMoney)
  const targetRaise = computed<Money>(() => (dealCard.value?.target_raise as Money | null) ?? initialMoney)
  const daysUntilClose = computed(() => {
    const d = dealCard.value?.expected_close_date
    if (!d) return 0
    return Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / 86400000))
  })

  const sinceInception = computed(() => capitalAccountStore.bookValue.since_inception)
  const closedCapital = computed<Money>(() => sinceInception.value.contributions ?? initialMoney)
  const mgmtFeeAmt = computed<Money>(() => sinceInception.value.management_fee ?? initialMoney)
  const otherCalledFee = computed<Money>(() => sinceInception.value.other_called_fee ?? initialMoney)
  const distributedFee = computed<Money>(() => sinceInception.value.other_distributed_fee ?? initialMoney)
  const carriedInterest = computed<Money>(() => sinceInception.value.carried_interest ?? initialMoney)
  const fundsDeployed = computed<Money>(() => entity.value?.insights?.initial_value ?? initialMoney)
  const totalDistributed = computed<Money>(() => sinceInception.value.distributions ?? initialMoney)
  const netDistributed = computed<Money>(() => entity.value?.insights?.distributed_net ?? initialMoney)
  const cashPosition = computed<Money>(() => entity.value?.insights?.cash_position ?? initialMoney)
  const currentValue = computed<Money>(() => entity.value?.insights?.current_value ?? initialMoney)
  const roi = computed<number>(() => entity.value?.insights?.roi ?? 0)
  const moic = computed<number>(() => entity.value?.insights?.moic ?? 0)
  const insightsXirr = computed<number | null>(() => {
    const xirr = entity.value?.insights?.xirr ?? null
    if (xirr != null && Math.abs(xirr) > 1000) return 0
    return xirr
  })
  const realizedGain = computed<Money>(() => sinceInception.value.realized_gain ?? initialMoney)
  const unrealizedGain = computed<Money>(() => sinceInception.value.unrealized_gain ?? initialMoney)
  const docsSignedCount = computed<number>(() => entity.value?.insights?.docs_signed_count ?? 0)
  const docsTotalCount = computed<number>(() => entity.value?.insights?.docs_total_count ?? 0)
  const paidCalled = computed<Money>(() => entity.value?.insights?.called_settled_total ?? initialMoney)
  const totalCalled = computed<Money>(() => entity.value?.insights?.capital_calls_total ?? initialMoney)

  const transactions = computed(() => transactionStore.transactions as any[])
  const chartInsights = useChartInsights(transactions, entity)

  const summaryLabel = computed(() => (isExited.value ? 'Exited On' : 'Closed On'))
  const summaryDate = computed(() => entity.value?.date_exited || (dealCard.value?.expected_close_date ?? ''))
  const summaryStatus = computed<'closed' | 'exited'>(() => (isExited.value ? 'exited' : 'closed'))

  const refetchCapitalAccount = () =>
    capitalAccountStore.fetchCapitalAccount('InvestorSet', entity_id, { slug, year: new Date().getFullYear() })

  const handleCloseSubmit = async (payload: { closing_documents: File[] }) => {
    await entityStore.closeEntity(entity_id, { slug, closing_documents: payload.closing_documents })
    await refetchCapitalAccount()
    await loadClosingDocuments()
  }

  const handleReopen = async () => {
    await entityStore.reopenEntity(entity_id, { slug })
    await refetchCapitalAccount()
  }

  const handleReclose = async () => {
    await entityStore.recloseEntity(entity_id, { slug })
    await refetchCapitalAccount()
  }

  const fetchExitReadiness = () => entityStore.fetchExitReadiness(entity_id, { slug })
  const fetchCloseReadiness = () => entityStore.fetchCloseReadiness(entity_id, { slug })

  const handleExitSubmit = async (payload: { exit_date: string; exit_type: string; exit_documents: File[] }) => {
    await entityStore.exitEntity(entity_id, { slug, ...payload })
    await refetchCapitalAccount()
    await loadExitDocuments()
  }

  const init = async () => {
    showSkeleton()
    try {
      await Promise.all([
        (async () => {
          await entityStore.fetchEntity(entity_id, { slug })
          await entityStore.fetchInsights(entity_id, { slug })
          if (dealCardFolderId.value) await filingStore.fetchFiles(dealCardFolderId.value)
          if (isClosed.value) await loadClosingDocuments()
          if (isExited.value) await loadExitDocuments()
        })(),
        capitalAccountStore.fetchCapitalAccount('InvestorSet', entity_id, { slug, year: new Date().getFullYear() }),
        transactionStore.fetchTransactions(entity_id, { slug }),
        entityStore.fetchMetricSeries(entity_id, { slug }),
      ])

      if (isAdmin.value && taskListId.value) {
        taskListStore.setSlug(slug)
        await taskListStore.fetchTaskList(taskListId.value)
      }
    } finally {
      hideSkeleton()
    }
  }

  onUnmounted(() => {
    entityStore.reset()
  })

  return {
    route: { entity_id, slug, entityType: entity_type },
    stores: { filingStore, taskListStore },
    state: { entity, isAdmin, isSiteAdmin, skeleton, init },
    taskList: { taskListId, isBillingSettled },
    lifecycle: { lifecycleState, isRaising, isClosed, isExited },
    deal: {
      dealCard,
      structure,
      isMemberView,
      hasStructure,
      showMetrics,
      investorCount,
      investmentCompletedCount,
      companyHref,
    },
    filing: { dealFiles, closingDocuments, exitDocuments },
    chartInsights,
    raising: { totalCommitted, targetRaise, daysUntilClose },
    chart: {
      metricSeriesPoints,
      metricSeriesCurrency,
      metricSeriesLoading,
    },
    metrics: {
      closedCapital,
      mgmtFeeAmt,
      otherCalledFee,
      distributedFee,
      carriedInterest,
      fundsDeployed,
      totalDistributed,
      netDistributed,
      cashPosition,
      currentValue,
      roi,
      moic,
      insightsXirr,
      realizedGain,
      unrealizedGain,
      docsSignedCount,
      docsTotalCount,
      paidCalled,
      totalCalled,
    },
    summary: { summaryLabel, summaryDate, summaryStatus },
    actions: {
      handleCloseSubmit,
      handleReopen,
      handleReclose,
      handleExitSubmit,
      fetchExitReadiness,
      fetchCloseReadiness,
    },
  }
}
